// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IADConfig, LoginStatus } from '../../../types/user';
import * as Msal from 'msal';
import { UserService } from '../../../services/user-service';
import { Observable } from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import { AuthService } from '../../../services/auth-service';
import {LOGIN_DEVICE_TOKEN, STAY_LOGGED_IN} from '../login/login.component';
import { Theme, ThemeService } from '../../../services/theme.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import {loadUser} from '../../store/user/user.actions';
import {AppState} from '../../store/app.state';
import {Store} from '@ngrx/store';
import { ConfigurationService } from '../../../services/configuration-service';
import { Partners } from '../../../types/partners';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-login-ms',
    templateUrl: './login-ms.component.html',
    styleUrls: ['./login-ms.component.scss'],
    standalone: false
})
export class LoginMsComponent implements OnInit {
  _showMsLogin = false;
  @Input()
  set showMsLogin(newVal: boolean) {
    this._showMsLogin = newVal;
    if ( this._showMsLogin && this.ngOnInit() ) {
      this.onMSClick();
    }
  }
  @Input() loginImmediately: boolean;
  @Input() authenticateCigentUser: boolean;
  @Output() toggleSigInClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() handleLogin: EventEmitter<any> = new EventEmitter<any>();

  msalInstance: Msal.UserAgentApplication;
  loginError = false;
  showADLogin = true;
  authParams = {scopes: ['User.Read']};
  domainError: { hasError: boolean; errorMessage: string | null}
  cigentLogin: boolean;
  errors = [];
  theme: Observable<Theme>;
  msButtonLightPath = '../../../assets/ms_signin_light_short.svg';
  msButtonDarkPath = '../../../assets/ms_signin_dark_short.svg';
  partnerId: Partners = Partners.Cigent;

  constructor(private userService: UserService,
              private authService: AuthService,
              private route: ActivatedRoute,
              private router: Router,
              private readonly store: Store<AppState>,
              private themeService: ThemeService,
              private configuration: ConfigurationService ) {
      this.errors = [];
      this.theme = this.themeService.getTheme();
      this.partnerId = this.configuration.partnerId ?? Partners.Cigent;
  }

  ngOnInit(): boolean {
    if (this.route.snapshot && this.route.snapshot.fragment && this.route.snapshot.fragment.startsWith('id_token=')) {
      const config = sessionStorage.getItem('cigent.msal_config');
      if (config) {
        // sessionStorage.removeItem('cigent.msal_config');
        this.msalInstance = new Msal.UserAgentApplication(JSON.parse(config));

        this.msalInstance.handleRedirectCallback((error, response) => {
          if (error) {
            this.showADLogin = true;
            this.loginError = true;
          } else {
            this.getToken();
          }
        });

        // on dev and staging this was causing duplicate login creation
        // this.getToken();
        return false;
      }
    } else {
      if ( this.loginImmediately ) {
            this.getADConfiguration();
      }
    }

    return true;
  }

  onDomainFocus() {
    this.clearDomainError();
  }

  clearDomainError(force = false) {
    if (this.errors.length || force) {
      this.errors = [];
    }
  }

  toggleSigIn() {
    this.clearDomainError(true);
    this.cigentLogin = !this.cigentLogin;
    this.toggleSigInClick.emit();
    this.clearForm();
  }

  clearForm() {
    this.loginError = false;
  }

  onMSClick() {
    this.getADConfiguration();
  }

  getADConfiguration() {
      const msalConfig: IADConfig = {} as IADConfig;
      this.userService.getADConfig()
          .subscribe(config => {
            msalConfig.auth = config;
            sessionStorage.setItem('cigent.msal_config', JSON.stringify(msalConfig));
            this.msalInstance = new Msal.UserAgentApplication(msalConfig);
            this.msalInstance.loginRedirect(this.authParams);
          },
          () => {
            this.errors.push({'notAuthorized': true});
      });
  }

    private loginWithToken(idToken: string) {
        sessionStorage.removeItem('cigent.msal_config');
        this.handleLogin.emit({id_token: idToken});
        if (!this.authenticateCigentUser) {
            this.showADLogin = false;
        } else {
            const stayLoggedIn = sessionStorage.getItem(STAY_LOGGED_IN).toLowerCase() === "true";
            const deviceToken = sessionStorage.getItem(LOGIN_DEVICE_TOKEN);

            this.userService.loginToAD(idToken, deviceToken, stayLoggedIn, this.partnerId)
            .subscribe(res => {
                    if (res?.status == LoginStatus.INVALID_DOMAIN) {
                        if (res?.login_url) {
                            window.location.href = res.login_url + '?invalid_domain=true';
                        }
                    } else if (deviceToken) {
                        sessionStorage.removeItem(LOGIN_DEVICE_TOKEN)
                        this.router.navigate(['/login-success']);
                    } else {
                        this.authService.login(res.token, res.mapToken, true, '');
                        // We do NOT want to destroy early, let this finish.
                        // Do NOT use .pipe(untilDestroyed(this))
                        this.store.dispatch(loadUser());
                    }
                },
                error => {
                    let validationError: { [key: string]: boolean } = {'notAuthorized': true};
                    if (error.toLowerCase() === 'conflict') {
                        validationError = {'cigentAccountExists': true};
                    }

                    this.showADLogin = true;
                    this.loginError = true;
                    this.errors.push(validationError);
                }
            );
        }
    }

  private getToken() {
    this.showADLogin = false;
    this.msalInstance.acquireTokenSilent(this.authParams).then(response => {
      this.loginWithToken(response.accessToken);
    }).catch(error => {
      this.showADLogin = true;
      // call acquireTokenPopup in case of acquireTokenSilent failure
      // due to consent or interaction required
      if (error.name === "InteractionRequiredAuthError") {
          this.msalInstance.acquireTokenPopup(this.authParams).then(response => {
              this.showADLogin = false;
              this.loginWithToken(response.accessToken);
          }).catch(() => {
              this.showADLogin = true;
              this.loginError = true;
          });
      }
    });
  }

  hasError(errorstring: string) {
    for (const error of this.errors) {
        if (error[errorstring]) {
          return true;
        }
    }
    return false;
  }
}
